import { action, computed, makeObservable, observable, observableRef } from 'mobx';

import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { EditorWindowState } from '../../components/editorWindow/editorWindowState';
import {
  clamp,
  GuidedTrainingDomSync,
  getGuidedTrainingCoachPosition,
  getGuidedTrainingStorageKey,
  getGuidedTrainingTargetRect,
  type GuidedTrainingCoachPosition,
  type GuidedTrainingRect,
} from '../../components/guidedTraining/guidedTrainingCore';

type PlayerGuidedTrainingRect = GuidedTrainingRect;
type CoachPosition = GuidedTrainingCoachPosition;

type Phase = 'player' | 'editor' | 'postReview';

type Step = {
  description: string;
  targetId?: string;
  primaryLabel: string;
  finalPlayerAction?: boolean;
  finalEditorAction?: boolean;
};

type Progress = {
  jobId: string;
  phase: Phase;
  currentStepIndex: number;
};

type Props = {
  hasNotesButton: () => boolean;
  hasReportButton: () => boolean;
  jobId: () => string | null;
  openReportEditor: () => Promise<void>;
  preparePostReviewStep: (stepIndex: number) => void;
  shouldStart: () => boolean;
  submitReportReview: () => Promise<boolean>;
};

const progressStoragePrefix = 'guidedTraining.player';
const activeBodyClass = 'player-guided-training-active';
const positionRetryDelayMs = 50;
const positionRetryAttempts = 14;

const getTargetRect = (targetId?: string): PlayerGuidedTrainingRect | null =>
  getGuidedTrainingTargetRect(targetId, { multiple: true });

const getCoachPosition = (rect: PlayerGuidedTrainingRect | null, phase: Phase): CoachPosition =>
  getGuidedTrainingCoachPosition(rect, {
    maxWidth: phase === 'editor' ? 360 : 420,
    height: phase === 'editor' ? 280 : 190,
    defaultTopRatio: 0.25,
    defaultTopMin: 110,
  });

export class PlayerGuidedTrainingState
  extends StoreNode {

  readonly nodeType = 'PlayerGuidedTraining';

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable<this, 'open' | 'complete' | 'resetRuntime' | 'restoreProgress'>(this, {
      isArmed: observable,
      isVisible: observable,
      phase: observable,
      currentStepIndex: observable,
      jobId: observable,
      targetRect: observableRef,
      coachPosition: observableRef,
      playerSteps: computed,
      editorSteps: computed,
      postReviewSteps: computed,
      steps: computed,
      currentStep: computed,
      totalSteps: computed,
      displayStepIndex: computed,
      allowTargetInteraction: computed,
      showBackButton: computed,
      handlePlayerReady: action,
      handlePlayerUnmounted: action,
      open: action,
      pause: action,
      skip: action,
      next: action,
      back: action,
      syncPosition: action,
      complete: action,
      resetRuntime: action,
      restoreProgress: action,
    });
    this.domSync = new GuidedTrainingDomSync({
      syncPosition: this.syncPosition,
      activeBodyClass,
      onKeyDown: this.handleKeyDown,
      retryAttempts: positionRetryAttempts,
      retryDelayMs: positionRetryDelayMs,
    });
  }

  isArmed = false;
  isVisible = false;
  phase: Phase = 'player';
  currentStepIndex = 0;
  jobId: string | null = null;
  targetRect: PlayerGuidedTrainingRect | null = null;
  coachPosition: CoachPosition = { top: 120, left: 16, width: 420 };

  private readonly domSync: GuidedTrainingDomSync;
  private hasBoundEditorWindowListener = false;

  get playerSteps(): Step[] {
    return [
      ...(this.getResolvedProp('hasNotesButton', false) ? [{
        description: 'Use Notes to review or add important context connected to this recording.',
        targetId: 'player-training-notes',
        primaryLabel: 'Next',
      }] : []),
      ...(this.getResolvedProp('hasReportButton', false) ? [{
        description: 'Open the generated First Draft beside Notes to begin reviewing the requested report.',
        targetId: 'player-training-report',
        primaryLabel: 'Next',
      }] : []),
      {
        description: 'The player keeps the source video, transcript, Notes, and generated report together.',
        targetId: 'player-training-video',
        primaryLabel: 'Open report editor',
        finalPlayerAction: true,
      },
    ];
  }

  get editorSteps(): Step[] {
    return [
      {
        description: 'Click inside the report and make any factual or wording edits before certification.',
        targetId: 'player-training-editor-body',
        primaryLabel: 'Next',
      },
      {
        description: 'CLIPr flags possible spelling and grammar improvements. See red squiggles under words and sentences and the red counter next to "Aa" in the upper right corner.',
        targetId: 'player-training-editor-window',
        primaryLabel: 'Next',
      },
      {
        description: 'Prompt CLIPr to improve this and future reports using this job specialty. Be direct: cut what\'s irrelevant, fix a name, tighten it up, or switch to first person. Edits made in the report are not saved when you regenerate.',
        targetId: 'player-training-feedback',
        primaryLabel: 'Next',
      },
      {
        description: 'Check this box to apply your prompt update to CLIPr to this report and all future reports.',
        targetId: 'player-training-revise',
        primaryLabel: 'Next',
      },
      {
        description: 'Only certify after reviewing the full draft. Certification locks the report as the approved version.',
        targetId: 'player-training-certify',
        primaryLabel: 'Certify & continue',
        finalEditorAction: true,
      },
    ];
  }

  get postReviewSteps(): Step[] {
    return [
      {
        description: 'Search for a name, phrase, or event and jump directly to that moment in the transcript.',
        targetId: 'player-training-search',
        primaryLabel: 'Next',
      },
      {
        description: 'Use Q&A to review automatically grouped questions and answers from the recording.',
        targetId: 'player-training-index',
        primaryLabel: 'Next',
      },
      {
        description: 'Open Topics Index to navigate the recording by detected subjects and subtopics.',
        targetId: 'player-training-index',
        primaryLabel: 'Complete training',
      },
    ];
  }

  get steps(): Step[] {
    switch (this.phase) {
      case 'editor':
        return this.editorSteps;
      case 'postReview':
        return this.postReviewSteps;
      default:
        return this.playerSteps;
    }
  }

  get currentStep(): Step | null {
    return this.steps[this.currentStepIndex] || null;
  }

  get totalSteps(): number {
    return this.steps.length;
  }

  get displayStepIndex(): number {
    return this.currentStepIndex + 1;
  }

  get allowTargetInteraction(): boolean {
    return this.phase === 'editor';
  }

  get showBackButton(): boolean {
    return this.currentStepIndex > 0;
  }

  private get currentJobId(): string | null {
    return this.getResolvedProp('jobId', null);
  }

  private get progressStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix);
  }

  private get completedStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix, 'completed');
  }

  get hasCompleted(): boolean {
    return !!this.store.storage.getLocal(this.completedStorageKey, true);
  }

  async handlePlayerReady(): Promise<boolean> {
    this.bindEditorWindowListener();

    const restoredProgress = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restoredProgress && !this.hasCompleted && this.getResolvedProp('shouldStart', false))
      this.open();

    if (!this.isArmed)
      return false;

    if (this.phase === 'editor')
      await this.openReportEditor();
    else if (this.phase === 'postReview')
      this.preparePostReviewStep();

    this.isVisible = true;
    this.domSync.bindListeners();
    this.saveProgress();
    this.domSync.schedulePositionSync(true);

    return true;
  }

  handlePlayerUnmounted() {
    if (!this.isArmed) return;
    this.pause();
  }

  private open() {
    this.clearProgress();
    this.resetRuntime();
    this.isArmed = true;
    this.isVisible = true;
    this.phase = 'player';
    this.currentStepIndex = 0;
    this.jobId = this.currentJobId;
    this.saveProgress();
  }

  pause = () => {
    if (this.isArmed)
      this.saveProgress();

    this.isVisible = false;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  // Marks the tour completed so it does not come back on the next visit.
  skip = () => this.complete();

  next = async () => {
    const step = this.currentStep;
    if (!step) return;

    if (step.finalPlayerAction) {
      this.phase = 'editor';
      this.currentStepIndex = 0;
      this.isVisible = false;
      this.saveProgress();
      await this.openReportEditor();
      this.isVisible = true;
      this.domSync.bindListeners();
      this.domSync.schedulePositionSync(true);
      return;
    }

    if (step.finalEditorAction) {
      const isRevisionRequested = this.store.editorWindow.canReviseReport
        && !!this.store.editorWindow.reviseEnabled.value;
      this.store.editorWindow.publicSafetyReportComplete.loadValue(true, true);
      const success = await this.submitReportReview();
      if (success) {
        if (isRevisionRequested) {
          this.saveProgress();
          this.isVisible = true;
          this.domSync.bindListeners();
          this.domSync.schedulePositionSync(true);
          return;
        }

        this.phase = 'postReview';
        this.currentStepIndex = 0;
        this.isVisible = false;
        this.saveProgress();
        this.store.editorWindow.close();
        this.preparePostReviewStep();
        this.isVisible = true;
        this.domSync.bindListeners();
        this.domSync.schedulePositionSync(true);
      } else {
        this.domSync.schedulePositionSync(true);
      }

      return;
    }

    if (this.phase === 'postReview' && this.currentStepIndex === this.steps.length - 1) {
      await this.complete();
      return;
    }

    this.currentStepIndex = Math.min(this.currentStepIndex + 1, this.steps.length - 1);
    this.saveProgress();
    if (this.phase === 'postReview')
      this.preparePostReviewStep();
    this.domSync.schedulePositionSync(true);
  }

  back = () => {
    if (this.currentStepIndex === 0) return;

    this.currentStepIndex -= 1;
    this.saveProgress();
    if (this.phase === 'postReview')
      this.preparePostReviewStep();
    this.domSync.schedulePositionSync(true);
  }

  syncPosition = () => {
    const rect = getTargetRect(this.currentStep?.targetId);
    this.targetRect = rect;
    this.coachPosition = getCoachPosition(rect, this.phase);

    if (!rect && this.currentStep?.targetId && this.domSync.canRetry)
      this.domSync.schedulePositionRetry();
  }

  private async complete() {
    this.store.storage.setLocal(this.completedStorageKey, true, true);
    this.clearProgress();
    this.resetRuntime();
  }

  private resetRuntime() {
    this.isArmed = false;
    this.isVisible = false;
    this.phase = 'player';
    this.currentStepIndex = 0;
    this.jobId = null;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  private async openReportEditor() {
    this.bindEditorWindowListener();
    await this.openReportEditorCallback();
  }

  private preparePostReviewStep() {
    const callback = this.getProp('preparePostReviewStep') as Props['preparePostReviewStep'];
    callback(this.currentStepIndex);
  }

  private async openReportEditorCallback() {
    const callback = this.getProp('openReportEditor') as Props['openReportEditor'];
    await callback();
  }

  private async submitReportReview() {
    const callback = this.getProp('submitReportReview') as Props['submitReportReview'];
    return await callback();
  }

  private bindEditorWindowListener() {
    if (this.hasBoundEditorWindowListener) return;

    const editorWindow = (this.store as Store & { editorWindow?: EditorWindowState }).editorWindow;
    if (!editorWindow) return;

    editorWindow.listen(this.editorWindowListener);
    this.hasBoundEditorWindowListener = true;
  }

  private saveProgress() {
    const jobId = this.jobId || this.currentJobId;
    if (!jobId) return;

    this.jobId = jobId;
    this.store.storage.setLocal(this.progressStorageKey, {
      jobId,
      phase: this.phase,
      currentStepIndex: this.currentStepIndex,
    }, true);
  }

  private loadProgress(): Progress | null {
    return this.store.storage.getLocal<Progress>(this.progressStorageKey, true);
  }

  private clearProgress() {
    this.store.storage.removeLocal(this.progressStorageKey);
  }

  private restoreProgress(): boolean {
    const progress = this.loadProgress();
    const jobId = this.currentJobId;
    if (!progress || !jobId || progress.jobId !== jobId)
      return false;

    this.jobId = progress.jobId;
    this.phase = progress.phase === 'editor'
      ? 'editor'
      : progress.phase === 'postReview' ? 'postReview' : 'player';
    this.currentStepIndex = clamp(progress.currentStepIndex, 0, this.steps.length - 1);
    this.isArmed = true;
    this.saveProgress();

    return true;
  }

  private handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape')
      this.pause();
  }

  private editorWindowListener = (msg: Message<EditorWindowState>) => {
    if (!this.isArmed || this.phase !== 'editor') return;

    switch (msg.type) {
      case 'open':
        this.isVisible = true;
        this.domSync.bindListeners();
        this.domSync.schedulePositionSync(true);
        break;
      case 'close':
        this.pause();
        break;
      default:
        break;
    }
  }
}
